import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { 
  Box, Container, BottomNavigation, BottomNavigationAction, 
  AppBar, Toolbar, Typography, IconButton, Tooltip, 
  Button, Badge
} from '@mui/material'
import { Home, Person, Brightness4, Brightness7, GetApp, Forum, AdminPanelSettings } from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { usePwaInstall } from '@/hooks/usePwaInstall'
import { useGroupNavigation } from '@/contexts/GroupNavigationContext'
import { PrayerIcon } from '@/components/PrayerIcon'
import { Logo } from '@/components/Logo'

const defaultNavItems = [
  { label: 'Home', icon: <Home />, path: '/' },
  { label: 'Profile', icon: <Person />, path: '/profile' },
]

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { mode, toggleTheme } = useTheme()
  const { canInstall, install } = usePwaInstall()
  const {
    groupId, activeTab, setActiveTab, leaveGroup,
    prayerBadgeCount, discussionBadgeCount,
  } = useGroupNavigation()

  // Determine if we're in a group-related context
  const isInGroup = groupId !== null && (
    location.pathname.startsWith(`/groups/${groupId}`) ||
    location.pathname.startsWith('/prayer-requests/') ||
    location.pathname.startsWith('/discussions/') ||
    location.pathname === '/profile'
  )

  // Clear group context when navigating to non-group routes
  useEffect(() => {
    if (groupId && !isInGroup) {
      leaveGroup()
    }
  }, [groupId, isInGroup, leaveGroup])

  // Compute current nav index
  let currentNav: number
  if (isInGroup) {
    if (location.pathname === '/profile') {
      currentNav = 2
    } else if (location.pathname.startsWith('/discussions/')) {
      currentNav = 1
    } else if (activeTab === 'discussions') {
      currentNav = 1
    } else {
      currentNav = 0
    }
  } else {
    currentNav = defaultNavItems.findIndex(item =>
      location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path))
    )
  }

  const handleGroupNavChange = (_: unknown, newValue: number) => {
    if (newValue === 0) {
      setActiveTab('prayers')
      if (!location.pathname.startsWith(`/groups/${groupId}`)) {
        navigate(`/groups/${groupId}`)
      }
    } else if (newValue === 1) {
      setActiveTab('discussions')
      if (!location.pathname.startsWith(`/groups/${groupId}`)) {
        navigate(`/groups/${groupId}`)
      }
    } else if (newValue === 2) {
      navigate('/profile')
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ minHeight: 48 }}>
          <Box
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => { leaveGroup(); navigate('/') }}
          >
            <Logo size={28} />
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 700 }}
            >
              ConnectLoop
            </Typography>
          </Box>
          {canInstall && (
            <Tooltip title="Install app">
              <Button variant='outlined' endIcon={<GetApp />} onClick={install} aria-label="Install app" color="inherit" sx={{ mr: 1.5 }}>
                Install App
              </Button>
            </Tooltip>
          )}
          {user?.isAdmin && (
            <Tooltip title="Admin Portal">
              <IconButton
                onClick={() => navigate('/admin')}
                color="inherit"
                size="small"
                aria-label="Admin portal"
                sx={{ mr: 0.5 }}
              >
                <AdminPanelSettings />
              </IconButton>
            </Tooltip>
          )}
          <IconButton 
            onClick={toggleTheme} 
            color="inherit"
            size="small"
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container 
        component="main" 
        maxWidth="sm" 
        sx={{ flex: 1, py: 2, px: { xs: 2, sm: 3 }, pb: user?.isAuthenticated ? 9 : 2 }}
      >
        <Outlet />
      </Container>

      {user?.isAuthenticated && (
        isInGroup ? (
          <BottomNavigation
            value={currentNav}
            onChange={handleGroupNavChange}
            showLabels
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderTop: 1,
              borderColor: 'divider',
              zIndex: 1100,
            }}
          >
            <BottomNavigationAction
              label="Prayers"
              icon={
                <Badge badgeContent={prayerBadgeCount} color="error">
                  <PrayerIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Discussions"
              icon={
                <Badge badgeContent={discussionBadgeCount} color="error">
                  <Forum />
                </Badge>
              }
            />
            <BottomNavigationAction label="Profile" icon={<Person />} />
          </BottomNavigation>
        ) : (
          <BottomNavigation
            value={currentNav}
            onChange={(_, newValue) => navigate(defaultNavItems[newValue].path)}
            showLabels
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderTop: 1,
              borderColor: 'divider',
              zIndex: 1100,
            }}
          >
            {defaultNavItems.map(item => (
              <BottomNavigationAction key={item.label} label={item.label} icon={item.icon} />
            ))}
          </BottomNavigation>
        )
      )}
    </Box>
  )
}
